import React, { useState } from "react";
import {
  Container,
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Modal,
  Button,
  AppBar,
  Toolbar,
  useScrollTrigger,
  Slide,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SubjectsChart from "../components/SubjectsChart";
import { MockBusinessProvider } from "../context/MockBusinessContext";
import { MockReviewsProvider } from "../context/MockReviewsContext";
import { ReactComponent as LogoTagline } from "../assets/logo.svg";
import ReviewsTable from "../components/ReviewsTable";
import { fakeBusinessData, fakeReviewsData } from "../constants/mockData";
import { List, ListItem } from "@mui/material";
import DashboardPreview from "../assets/Dashboard.png";
import ReviewsPreview from "../assets/Reviews.png";
import StarIcon from '@mui/icons-material/Star';
import { ReactComponent as LinkedInIcon } from "../assets/linkedin.svg";
import { ReactComponent as XIcon } from "../assets/x.svg";
import { ReactComponent as EmailIcon } from "../assets/email.svg";
import SignupForm from '../components/SignupForm';

// Hide AppBar on scroll
function HideOnScroll(props) {
  const { children } = props;
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const Landing = () => {
  const [localView, setLocalView] = useState("Sales");
  const [selectedImage, setSelectedImage] = useState(null);

  // Handle modal open/close
  const handleImageClick = (imageSrc) => {
    setSelectedImage(imageSrc);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  // Smooth scroll to signup section
  const scrollToSignup = (e) => {
    e.preventDefault();
    const signupSection = document.getElementById('signup-section');
    if (signupSection) {
      signupSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <MockBusinessProvider>
      <MockReviewsProvider>
        {/* Sticky Navigation */}
        <HideOnScroll>
          <AppBar
            position="fixed"
            elevation={0}
            sx={{
              background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%)',
              backdropFilter: 'blur(20px)',
              borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
              boxShadow: '0 4px 30px rgba(0, 0, 0, 0.03)'
            }}
          >
            <Toolbar
              sx={{
                justifyContent: 'space-between',
                py: { xs: 0.5, md: 1 },
                px: { xs: 2, md: 3 },
                minHeight: { xs: '56px', md: '64px' }
              }}
            >
              <LogoTagline style={{
                width: 'auto',
                height: '36px',
                maxWidth: '220px'
              }} />
              <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 } }}>
                <Button
                  color="primary"
                  sx={{
                    mr: { xs: 1, md: 2 },
                    px: { xs: 2, md: 3 },
                    py: { xs: 0.5, md: 1 },
                    fontSize: { xs: '0.875rem', md: '0.95rem' },
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.08)'
                    }
                  }}
                  onClick={() => window.location.href = '/login'}
                >
                  Log in
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    px: { xs: 2, md: 3 },
                    py: { xs: 0.5, md: 1 },
                    fontSize: { xs: '0.875rem', md: '0.95rem' },
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565C0 100%)',
                    boxShadow: '0 4px 14px 0 rgba(25, 118, 210, 0.3)',
                    borderRadius: '50px',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                      boxShadow: '0 6px 20px 0 rgba(25, 118, 210, 0.4)',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                  onClick={scrollToSignup}
                >
                  See analysis for free
                </Button>
              </Box>
            </Toolbar>
          </AppBar>
        </HideOnScroll>
        <Toolbar /> {/* Spacer for fixed AppBar */}

        {/* Main Content */}
        <Box
          sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            opacity: 0,
            animation: 'fadeIn 1s forwards',
            '@keyframes fadeIn': {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
        >
          {/* Hero Section */}
          <Box
            sx={{
              pt: { xs: 12, md: 16 },
              pb: { xs: 10, md: 14 },
              textAlign: 'center',
              background: 'linear-gradient(180deg, #f8f9fe 0%, rgba(255, 255, 255, 0.95) 100%)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-50%',
                width: '200%',
                height: '100%',
                background: 'radial-gradient(circle at center, rgba(25, 118, 210, 0.03) 0%, transparent 70%)',
                zIndex: 0
              }
            }}
          >
            <Container maxWidth="md">
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  fontSize: { xs: '2.25rem', md: '3.25rem' },
                  lineHeight: 1.2,
                  background: 'linear-gradient(135deg, #0A2540 0%, #1976d2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em'
                }}
              >
                Every 1-Star Change in Rating Affects Your Revenue by 10%
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 2,
                  maxWidth: '800px',
                  mx: 'auto',
                  color: '#546E7A',
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  lineHeight: 1.5,
                  fontWeight: 500
                }}
              >
                {/* Research indicates that every 1-star change in rating affects your total revenue by up to 10%. */}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 5,
                  maxWidth: '800px',
                  mx: 'auto',
                  color: '#546E7A',
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                  lineHeight: 1.5,
                  fontWeight: 400
                }}
              >
                Our AI analyzes your Google reviews to reveal exactly what drives your ratings and revenue, transforming customer feedback into actionable growth opportunities with personalized recommended actions.
              </Typography>
              <Button
                variant="contained"
                size="large"
                sx={{
                  py: 2.5,
                  px: 6,
                  fontSize: '1.25rem',
                  textTransform: 'none',
                  borderRadius: '50px',
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565C0 100%)',
                  boxShadow: '0 8px 20px -6px rgba(25, 118, 210, 0.5)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                    boxShadow: '0 12px 24px -8px rgba(25, 118, 210, 0.6)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
                onClick={scrollToSignup}
              >
                See analysis for free
              </Button>
              <Typography
                variant="body1"
                sx={{
                  mt: 3,
                  color: '#78909C',
                  fontSize: '1rem'
                }}
              >
                Start transforming your customer feedback into growth today
              </Typography>
            </Container>
          </Box>

          {/* Main Container - Features/Demonstration */}
          <Container maxWidth="xl" sx={{
            mt: { xs: 6, md: 8 },
            mb: { xs: 6, md: 8 },
            position: 'relative'
          }}>
            <Grid container spacing={4} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Box sx={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                  borderRadius: '24px',
                  p: { xs: 3, md: 4 },
                  boxShadow: '0 20px 40px -12px rgba(0,0,0,0.1)',
                  height: '100%',
                  border: '1px solid rgba(25, 118, 210, 0.1)',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 24px 48px -12px rgba(0,0,0,0.15)'
                  }
                }}>
                  <Typography variant="h4" component="h2" sx={{
                    mb: 4,
                    fontWeight: 700,
                    color: '#0A2540',
                    letterSpacing: '-0.02em'
                  }}>
                    AI-Powered Revenue Insights
                  </Typography>
                  <Box sx={{
                    mb: 4,
                    p: 3,
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.04) 100%)',
                    border: '1px solid rgba(25, 118, 210, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}>
                    <Box sx={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #1976d2 0%, #1565C0 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <StarIcon sx={{ color: 'white', fontSize: '28px' }} />
                    </Box>
                    <Box>
                      <Typography sx={{
                        color: '#0A2540',
                        fontSize: '1.25rem',
                        fontWeight: 600,
                        mb: 0.5
                      }}>
                        Ratings Drive Revenue
                      </Typography>
                      <Typography sx={{
                        color: '#546E7A',
                        fontSize: '1rem',
                        lineHeight: 1.5
                      }}>
                        Research shows that every 1-star change in rating affects total revenue by up to 10%.<sup>1</sup>
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body1" sx={{
                    mb: 3,
                    color: '#546E7A',
                    fontSize: '1.1rem',
                    lineHeight: 1.6
                  }}>
                    Our advanced AI analyzes every review to identify significant revenue opportunities and untapped growth potential for your business.
                  </Typography>

                  {/* Reviews Table with enhanced styling */}
                  <Box sx={{
                    backgroundColor: 'transparent',
                    borderRadius: '12px',
                    p: 3,
                    mb: 4,
                    boxShadow: 'none',
                    border: 'none'
                  }}>
                    <ReviewsTable
                      reviewsData={fakeReviewsData.slice(0, 5)}
                      currencySymbol={fakeBusinessData.currencySymbol}
                      view={localView}
                      selectedBusiness={fakeBusinessData.selectedBusiness}
                      showColoredText={true}
                      showDateColumn={false}
                      showSearch={false}
                      showBusinessName={false}
                    />
                  </Box>

                  {/* Chart Section */}
                  <Box sx={{
                    height: 400,
                    backgroundColor: 'transparent',
                    borderRadius: '12px',
                    p: 3,
                    mb: 4,
                    boxShadow: 'none',
                    border: 'none'
                  }}>
                    <SubjectsChart
                      businessData={fakeBusinessData}
                      reviewsDataOverride={fakeReviewsData}
                      viewOverride={localView}
                      showTitle={true}
                      showSearch={false}
                    />
                  </Box>

                  {/* Toggle Button Group with enhanced styling */}
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="body1" sx={{
                      mb: 2,
                      color: 'rgba(0, 0, 0, 0.7)',
                      fontSize: '1.1rem',
                      lineHeight: 1.6
                    }}>
                      Toggle between Revenue Impact and Rating Impact to see different perspectives.
                    </Typography>
                    <ToggleButtonGroup
                      value={localView}
                      exclusive
                      onChange={(event, newView) => {
                        if (newView !== null) {
                          setLocalView(newView);
                        }
                      }}
                      sx={{
                        border: '1px solid rgba(25, 118, 210, 0.2)',
                        borderRadius: '12px',
                        backgroundColor: 'white',
                        p: 0.5,
                        width: '100%',
                        maxWidth: '400px',
                        boxShadow: '0 4px 12px -6px rgba(0,0,0,0.1)',
                        '& .MuiToggleButton-root': {
                          border: 'none',
                          borderRadius: '8px !important',
                          flex: 1,
                          py: 1.5,
                          color: 'rgba(0, 0, 0, 0.7)',
                          fontSize: '1rem',
                          fontWeight: 500,
                          '&.Mui-selected': {
                            backgroundColor: '#1565C0',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: '#1565C0',
                            }
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)'
                          }
                        }
                      }}
                    >
                      <ToggleButton value="Sales">
                        Revenue
                      </ToggleButton>
                      <ToggleButton value="Rating">
                        Rating
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>
              </Grid>

              {/* Preview Images Section */}
              <Grid item xs={12} md={6}>
                <Box sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '16px',
                  p: { xs: 3, md: 4 },
                  backdropFilter: 'blur(20px)',
                  boxShadow: '0 20px 40px -12px rgba(0,0,0,0.1)',
                  height: '100%',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4
                }}>
                  <Typography variant="h4" component="h2" sx={{
                    mb: 2,
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #1565C0 0%, #42a5f5 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    letterSpacing: '-0.02em'
                  }}>
                    Preview the Dashboard
                  </Typography>

                  <Box
                    component="div"
                    onClick={() => handleImageClick(DashboardPreview)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      boxShadow: '0 12px 24px -8px rgba(0,0,0,0.15)',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.02) translateY(-4px)',
                        boxShadow: '0 20px 40px -12px rgba(0,0,0,0.2)'
                      }
                    }}
                  >
                    <img
                      src={DashboardPreview}
                      alt="Dashboard Preview"
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block'
                      }}
                    />
                  </Box>
                  <Box
                    component="div"
                    onClick={() => handleImageClick(ReviewsPreview)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      boxShadow: '0 12px 24px -8px rgba(0,0,0,0.15)',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.02) translateY(-4px)',
                        boxShadow: '0 20px 40px -12px rgba(0,0,0,0.2)'
                      }
                    }}
                  >
                    <img
                      src={ReviewsPreview}
                      alt="Reviews Preview"
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block'
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>

          {/* Benefits Section */}
          <Box
            id="features"
            sx={{
              py: { xs: 8, md: 12 },
              background: 'linear-gradient(180deg, #f8f9fe 0%, #ffffff 100%)',
              borderTop: '1px solid rgba(25, 118, 210, 0.08)',
              borderBottom: '1px solid rgba(25, 118, 210, 0.08)'
            }}>
            <Container maxWidth="lg">
              <Typography
                variant="h3"
                align="center"
                sx={{
                  mb: { xs: 6, md: 8 },
                  fontWeight: 800,
                  fontSize: { xs: '2rem', md: '2.75rem' },
                  color: '#0A2540',
                  letterSpacing: '-0.02em'
                }}
              >
                Transform Your Business with AI-Powered Insights
              </Typography>
              <Grid container spacing={4} justifyContent="center">
                <Grid item xs={12} md={4}>
                  <Box sx={{
                    p: 4,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                    borderRadius: '16px',
                    height: '100%',
                    boxShadow: '0 20px 40px -12px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(25, 118, 210, 0.1)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 24px 48px -12px rgba(0,0,0,0.15)'
                    }
                  }}>
                    <Typography variant="h6" sx={{
                      mb: 2,
                      fontWeight: 700,
                      color: '#0A2540',
                      fontSize: '1.25rem'
                    }}>
                      Unlock Revenue Opportunities
                    </Typography>
                    <Typography variant="body1" sx={{
                      color: '#546E7A',
                      fontSize: '1.1rem',
                      lineHeight: 1.6
                    }}>
                      Our AI analyzes your Google reviews to identify specific areas where improvements could directly impact your bottom line.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{
                    p: 4,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                    borderRadius: '16px',
                    height: '100%',
                    boxShadow: '0 20px 40px -12px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(25, 118, 210, 0.1)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 24px 48px -12px rgba(0,0,0,0.15)'
                    }
                  }}>
                    <Typography variant="h6" sx={{
                      mb: 2,
                      fontWeight: 700,
                      color: '#0A2540',
                      fontSize: '1.25rem'
                    }}>
                      Data-Driven Decisions
                    </Typography>
                    <Typography variant="body1" sx={{
                      color: '#546E7A',
                      fontSize: '1.1rem',
                      lineHeight: 1.6
                    }}>
                      Make informed business decisions based on concrete data and AI-powered analysis of customer sentiment and feedback patterns.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{
                    p: 4,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                    borderRadius: '16px',
                    height: '100%',
                    boxShadow: '0 20px 40px -12px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(25, 118, 210, 0.1)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 24px 48px -12px rgba(0,0,0,0.15)'
                    }
                  }}>
                    <Typography variant="h6" sx={{
                      mb: 2,
                      fontWeight: 700,
                      color: '#0A2540',
                      fontSize: '1.25rem'
                    }}>
                      Actionable Recommendations
                    </Typography>
                    <Typography variant="body1" sx={{
                      color: '#546E7A',
                      fontSize: '1.1rem',
                      lineHeight: 1.6
                    }}>
                      Get AI-powered recommended actions prioritized by revenue impact, making it clear exactly what steps to take to improve your business performance.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </Box>

          {/* Signup Form Section */}
          <Box 
            id="signup-section"
            sx={{
              py: { xs: 8, md: 12 },
              background: 'linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%)',
              borderBottom: '1px solid rgba(25, 118, 210, 0.08)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-50%',
                width: '200%',
                height: '100%',
                background: 'radial-gradient(circle at center, rgba(25, 118, 210, 0.04) 0%, transparent 70%)',
                zIndex: 0
              }
            }}
          >
            <Container maxWidth="lg">
              <Typography
                variant="h3"
                align="center"
                sx={{
                  mb: { xs: 4, md: 6 },
                  fontWeight: 800,
                  fontSize: { xs: '2rem', md: '2.75rem' },
                  background: 'linear-gradient(135deg, #0A2540 0%, #1976d2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em'
                }}
              >
                Get Started for Free
              </Typography>
              <Typography
                variant="h5"
                align="center"
                sx={{
                  mb: { xs: 6, md: 8 },
                  maxWidth: '800px',
                  mx: 'auto',
                  color: '#546E7A',
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                  lineHeight: 1.5,
                  fontWeight: 400
                }}
              >
                Create your account now and start transforming your customer feedback into actionable insights
              </Typography>
              <Box sx={{ maxWidth: '600px', mx: 'auto' }}>
                <SignupForm />
              </Box>
            </Container>
          </Box>

          {/* FAQ Section */}
          <Box sx={{
            py: { xs: 8, md: 12 },
            background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fe 100%)'
          }}>
            <Container maxWidth="md">
              <Typography
                variant="h3"
                align="center"
                sx={{
                  mb: { xs: 6, md: 8 },
                  fontWeight: 800,
                  fontSize: { xs: '2rem', md: '2.75rem' },
                  color: '#0A2540',
                  letterSpacing: '-0.02em'
                }}
              >
                Common Questions
              </Typography>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <Box sx={{
                    mb: 4,
                    p: 4,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                    borderRadius: '16px',
                    boxShadow: '0 8px 32px -12px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(25, 118, 210, 0.1)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 40px -12px rgba(0,0,0,0.15)'
                    }
                  }}>
                    <Typography variant="h6" sx={{
                      mb: 2,
                      fontWeight: 700,
                      color: '#0A2540',
                      fontSize: '1.25rem'
                    }}>
                      How accurate are the revenue predictions?
                    </Typography>
                    <Typography variant="body1" sx={{
                      color: '#546E7A',
                      fontSize: '1.1rem',
                      lineHeight: 1.6
                    }}>
                      Research shows that every 1-star change in rating affects total revenue by up to 10%. Our AI analyzes reviews to classify comments and extract key subjects and sentiment, then calculates revenue impact by attributing each review's contribution to the overall rating. Revenue predictions are based on extensive research demonstrating the direct relationship between online ratings and business revenue.
                    </Typography>
                    <Typography variant="body2" sx={{
                      mt: 1.5,
                      color: '#78909C',
                      fontSize: '0.9rem',
                      fontStyle: 'italic'
                    }}>
                      <sup>1</sup> Based on research by Michael Luca (2011), "Reviews, Reputation, and Revenue: The Case of Yelp.com," Harvard Business School Working Paper, which found that a 1-star change in rating affected independent restaurant revenue by 5-9%. Given technology's increasing influence on consumer behavior since 2011, we estimate current impact on business revenue to be up to 10%.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{
                    mb: 4,
                    p: 4,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                    borderRadius: '16px',
                    boxShadow: '0 8px 32px -12px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(25, 118, 210, 0.1)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 40px -12px rgba(0,0,0,0.15)'
                    }
                  }}>
                    <Typography variant="h6" sx={{
                      mb: 2,
                      fontWeight: 700,
                      color: '#0A2540',
                      fontSize: '1.25rem'
                    }}>
                      What makes this different from other review tools?
                    </Typography>
                    <Typography variant="body1" sx={{
                      color: '#546E7A',
                      fontSize: '1.1rem',
                      lineHeight: 1.6
                    }}>
                      Unlike basic sentiment analysis tools, we calculate the exact revenue impact of each Google review and provide specific recommended actions. Our AI goes beyond simple positive/negative categorization to identify specific revenue opportunities and prioritize them by ROI potential.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{
                    mb: 4,
                    p: 4,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                    borderRadius: '16px',
                    boxShadow: '0 8px 32px -12px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(25, 118, 210, 0.1)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 40px -12px rgba(0,0,0,0.15)'
                    }
                  }}>
                    <Typography variant="h6" sx={{
                      mb: 2,
                      fontWeight: 700,
                      color: '#0A2540',
                      fontSize: '1.25rem'
                    }}>
                      How quickly will I see results?
                    </Typography>
                    <Typography variant="body1" sx={{
                      color: '#546E7A',
                      fontSize: '1.1rem',
                      lineHeight: 1.6
                    }}>
                      Our AI-powered platform provides immediate insights from your review data, helping you identify patterns and opportunities in your customer feedback. The platform continuously analyzes new reviews to help you track improvements and measure the impact of changes you implement based on the insights provided.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{
                    mb: 4,
                    p: 4,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                    borderRadius: '16px',
                    boxShadow: '0 8px 32px -12px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(25, 118, 210, 0.1)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 40px -12px rgba(0,0,0,0.15)'
                    }
                  }}>
                    <Typography variant="h6" sx={{
                      mb: 2,
                      fontWeight: 700,
                      color: '#0A2540',
                      fontSize: '1.25rem'
                    }}>
                      Is this only for large businesses?
                    </Typography>
                    <Typography variant="body1" sx={{
                      color: '#546E7A',
                      fontSize: '1.1rem',
                      lineHeight: 1.6
                    }}>
                      Not at all! We work with businesses of all sizes. Our AI adapts to your business scale and industry. Whether you're a single location startup or a multi-location enterprise, we help you maximize the revenue impact of every review.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{
                    mb: 4,
                    p: 4,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
                    borderRadius: '16px',
                    boxShadow: '0 8px 32px -12px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(25, 118, 210, 0.1)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 40px -12px rgba(0,0,0,0.15)'
                    }
                  }}>
                    <Typography variant="h6" sx={{
                      mb: 2,
                      fontWeight: 700,
                      color: '#0A2540',
                      fontSize: '1.25rem'
                    }}>
                      How does your pricing work?
                    </Typography>
                    <Typography variant="body1" sx={{
                      color: '#546E7A',
                      fontSize: '1.1rem',
                      lineHeight: 1.6
                    }}>
                      We offer a 14-day free trial giving you full access to all features. After the trial, you can choose to subscribe to continue receiving ongoing analysis for your business, competitors, or any other business with Google reviews.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </Box>

          {/* Enhanced Modal */}
          <Modal
            open={Boolean(selectedImage)}
            onClose={handleCloseModal}
            aria-labelledby="image-modal"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
              backdropFilter: 'blur(8px)'
            }}
          >
            <Box
              sx={{
                position: 'relative',
                maxWidth: '95vw',
                maxHeight: '95vh',
                overflow: 'hidden',
                backgroundColor: 'white',
                borderRadius: '16px',
                boxShadow: '0 24px 48px -12px rgba(0,0,0,0.3)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '&:focus': {
                  outline: 'none',
                },
                animation: 'modalFadeIn 0.3s ease-out',
                '@keyframes modalFadeIn': {
                  from: {
                    opacity: 0,
                    transform: 'scale(0.95)'
                  },
                  to: {
                    opacity: 1,
                    transform: 'scale(1)'
                  }
                }
              }}
              onClick={handleCloseModal}
            >
              {selectedImage && (
                <img
                  src={selectedImage}
                  alt="Enlarged preview"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block'
                  }}
                />
              )}
            </Box>
          </Modal>

          {/* Footer */}
          <Box
            component="footer"
            sx={{
              py: { xs: 8, md: 10 },
              background: 'linear-gradient(180deg, #f8f9fe 0%, #ffffff 100%)',
              borderTop: '1px solid rgba(25, 118, 210, 0.08)',
              mt: 'auto'
            }}
          >
            <Container maxWidth="lg">
              <Grid container spacing={6} alignItems="flex-start">
                <Grid item xs={12} md={4}>
                  <Box sx={{
                    mb: { xs: 4, md: 0 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    position: 'static',
                    backgroundColor: 'transparent'
                  }}>
                    <Typography variant="body1" sx={{
                      color: '#546E7A',
                      fontSize: '1.1rem',
                      lineHeight: 1.6,
                      mb: 3,
                      maxWidth: '400px'
                    }}>
                      Transforming customer feedback into measurable revenue growth through AI-powered insights.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Grid container spacing={4}>
                    <Grid item xs={6} md={6} sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: { xs: 'flex-start', md: 'center' }
                    }}>
                      <Typography variant="subtitle1" sx={{
                        fontWeight: 700,
                        color: '#0A2540',
                        mb: 3,
                        textAlign: { xs: 'left', md: 'center' }
                      }}>
                        Quick Links
                      </Typography>
                      <List sx={{
                        p: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: { xs: 'flex-start', md: 'center' }
                      }}>
                        <ListItem sx={{ p: 0, mb: 2 }}>
                          <Button
                            component="a"
                            href="#features"
                            sx={{
                              color: '#546E7A',
                              p: 0,
                              textTransform: 'none',
                              fontSize: '1rem',
                              '&:hover': {
                                backgroundColor: 'transparent',
                                color: '#1976d2',
                                transform: 'translateX(4px)',
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            Features
                          </Button>
                        </ListItem>
                        <ListItem sx={{ p: 0, mb: 2 }}>
                          <Button
                            component="a"
                            href="/about"
                            sx={{
                              color: '#546E7A',
                              p: 0,
                              textTransform: 'none',
                              fontSize: '1rem',
                              '&:hover': {
                                backgroundColor: 'transparent',
                                color: '#1976d2',
                                transform: 'translateX(4px)',
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            About
                          </Button>
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={6} md={6} sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: { xs: 'flex-start', md: 'center' }
                    }}>
                      <Typography variant="subtitle1" sx={{
                        fontWeight: 700,
                        color: '#0A2540',
                        mb: 3,
                        textAlign: { xs: 'left', md: 'center' }
                      }}>
                        Contact Us
                      </Typography>
                      <List sx={{
                        p: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: { xs: 'flex-start', md: 'center' }
                      }}>
                        <ListItem sx={{ p: 0, mb: 2 }}>
                          <Button
                            sx={{
                              color: '#546E7A',
                              p: 0,
                              textTransform: 'none',
                              fontSize: '1rem',
                              '&:hover': {
                                backgroundColor: 'transparent',
                                color: '#1976d2',
                                transform: 'translateX(4px)',
                              },
                              transition: 'all 0.2s ease'
                            }}
                            href="mailto:info@sashy.ai"
                          >
                            Get Support
                          </Button>
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Box sx={{
                mt: 8,
                pt: 4,
                borderTop: '1px solid rgba(25, 118, 210, 0.08)',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
              }}>
                {/* Creator Information Section */}
                <Box sx={{
                  mb: 6,
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'center', md: 'flex-start' },
                  gap: 4,
                  p: 3,
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.02) 100%)',
                  border: '1px solid rgba(25, 118, 210, 0.08)'
                }}>
                  <Box sx={{
                    width: { xs: '150px', md: '180px' },
                    height: { xs: '150px', md: '180px' },
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '3px solid #1976d2',
                    boxShadow: '0 8px 24px -8px rgba(25, 118, 210, 0.4)'
                  }}>
                    <img 
                      src="/robert.jpg" 
                      alt="Robert Whiteley" 
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://via.placeholder.com/180?text=Robert";
                      }}
                    />
                  </Box>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    textAlign: { xs: 'center', md: 'left' }
                  }}>
                    <Typography variant="h5" sx={{
                      mb: 2,
                      fontWeight: 700,
                      color: '#0A2540'
                    }}>
                      Hey! I'm Robert
                    </Typography>
                    <Typography variant="body1" sx={{
                      color: '#546E7A',
                      fontSize: '1.1rem',
                      lineHeight: 1.6,
                      mb: 3
                    }}>
                      I built this app! Feel free to reach out to me with any suggestions, improvements, bug fixes or just to say that you liked or didn't like it!
                    </Typography>
                    <Typography variant="body1" sx={{
                      color: '#546E7A',
                      fontSize: '1.1rem',
                      lineHeight: 1.6,
                      mb: 3
                    }}>
                      As an aspiring entrepreneur myself, and previously Business Development Manager, I love helping small businesses, learning about them and working out how to grow their revenue.
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      gap: 2,
                      mt: 1,
                      justifyContent: { xs: 'center', md: 'flex-start' }
                    }}>
                      <Box
                        component="a"
                        href="mailto:info@sashy.ai"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#1976d2',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            transition: 'transform 0.2s ease'
                          }
                        }}
                      >
                        <EmailIcon style={{ width: '28px', height: '28px' }} />
                      </Box>
                      <Box
                        component="a"
                        href="https://www.linkedin.com/in/robert-whiteley/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#0077B5',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            transition: 'transform 0.2s ease'
                          }
                        }}
                      >
                        <LinkedInIcon style={{ width: '28px', height: '28px' }} />
                      </Box>
                      <Box
                        component="a"
                        href="https://x.com/rcwhiteley"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#000000',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            transition: 'transform 0.2s ease'
                          }
                        }}
                      >
                        <XIcon style={{ width: '28px', height: '28px' }} />
                      </Box>
                    </Box>
                  </Box>
                </Box>
                
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2
                }}>
                  <Typography variant="body2" sx={{
                    color: '#78909C',
                    fontSize: '0.95rem'
                  }}>
                    © {new Date().getFullYear()} Sashy. All rights reserved.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Button
                      sx={{
                        color: '#78909C',
                        p: 0,
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: '#1976d2'
                        }
                      }}
                      href="/privacy"
                    >
                      Privacy Policy
                    </Button>
                    <Button
                      sx={{
                        color: '#78909C',
                        p: 0,
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: '#1976d2'
                        }
                      }}
                      href="/terms"
                    >
                      Terms of Service
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Container>
          </Box>
        </Box>
      </MockReviewsProvider>
    </MockBusinessProvider>
  );
};

export default Landing;
